import { memo, useCallback, useEffect, useMemo } from "react"
import { create } from "zustand"
import { ThemeProvider } from "styled-components"
import { setCssVars } from "@repo/utils"
import {
	initialPalette,
	PLEO_PREFIX,
	THEME_MODES,
	theme as initialTheme,
	DEFAULT_COLOR_SCHEME,
	DEFAULT_MODE,
} from "../../styles/themeSettings"
import { shallow } from "zustand/shallow"
import Typography from "../../styles/Typography"
import Paddings from "../../styles/Paddings"
import GlobalStyles from "../../styles/globalStyles"
import useMobileDevice from "../hooks/useMobileDevice"
import { useIsDesktop } from "../providers/DeviceProvider"
import { useNavigation } from "../providers/NavigationProvider"

const useThemeStore = create((set, getState) => ({
	mode: DEFAULT_MODE,
	colorScheme: DEFAULT_COLOR_SCHEME,
	palette: initialTheme.palette,
	setTheme: (props = { mode: DEFAULT_MODE, colorScheme: DEFAULT_COLOR_SCHEME, palette: initialTheme.palette }) => {
		const newState = { ...getState() }
		if (props.mode) {
			localStorage.setItem("theme", props.mode)
			newState.mode = props.mode
		}
		if (props.colorScheme) {
			newState.colorScheme = props.colorScheme
		}
		if (props.palette) {
			newState.palette = props.palette
		}
		set(newState)
	},
}))

function getColorPalette(palette, colorScheme = DEFAULT_COLOR_SCHEME) {
	return {
		primary: palette[colorScheme]?.primary || initialPalette.light[colorScheme].primary,
		secondary: palette[colorScheme]?.secondary || initialPalette.light[colorScheme].secondary,
		tertiary: palette[colorScheme]?.tertiary || initialPalette.light[colorScheme].tertiary,
	}
}

function setColorPalette(palette) {
	return setCssVars(document.documentElement, {
		"primary-color": palette?.primary,
		"secondary-color": palette?.secondary,
		"tertiary-color": palette?.tertiary,
	})
}

function validateCmsTheme(theme) {
	return {
		main: {
			primary: theme?.mainPrimary?.hex,
			secondary: theme?.mainSecondary?.hex,
			tertiary: theme?.mainTertiary?.hex,
		},
		extra: {
			primary: theme?.extraPrimary?.hex,
			secondary: theme?.extraSecondary?.hex,
			tertiary: theme?.extraTertiary?.hex,
		},
	}
}

export function resolvePalette({ mode, colorScheme, darkTheme, lightTheme }) {
	if (mode === THEME_MODES.A11Y) {
		return getColorPalette(initialPalette, mode)
	} else {
		const cmsTheme = mode === THEME_MODES.LIGHT ? lightTheme : darkTheme
		const pleoTheme = colorScheme.includes(PLEO_PREFIX)
		const cs = pleoTheme ? colorScheme.substring(PLEO_PREFIX.length) : colorScheme
		const colors = pleoTheme || !cmsTheme ? initialPalette[mode] : validateCmsTheme(cmsTheme)
		return getColorPalette(colors, cs)
	}
}

const Theme = ({ children, darkTheme = null, lightTheme = null, forceLight = false }) => {
	const { mode, colorScheme, palette, setTheme } = useThemeStore()
	const isMobileDevice = useMobileDevice()
	const isDesktop = useIsDesktop()
	// const visible3D = useVisible3D();
	const isLoaded = useNavigation((state) => state.isLoaded)

	// const isMobileDevice = false;
	// const isDesktop = true;
	const visible3D = true
	// const isLoaded = true

	const context = useMemo(
		() => ({
			...initialTheme,
			isDesktop,
			isMobileDevice,
			visible3D,
			setTheme,
		}),
		[isMobileDevice, isDesktop, visible3D, setTheme],
	)

	useEffect(() => {
		if (!forceLight) {
			const previousTheme = localStorage.getItem("theme")
			if (!previousTheme || previousTheme === "null") {
				const media = window.matchMedia("(prefers-color-scheme: dark)")
				if (media.matches && mode !== THEME_MODES.DARK) {
					setTheme({ mode: THEME_MODES.DARK })
				}
			} else if (mode !== previousTheme) {
				setTheme({ mode: previousTheme })
			}
		}
	}, [forceLight, mode, setTheme])

	const updatePalette = useCallback(
		({ mode, colorScheme, darkTheme, lightTheme }) => {
			const newPalette = resolvePalette({ mode, colorScheme, darkTheme, lightTheme })
			setTheme({ palette: newPalette })
		},
		[setTheme],
	)

	useEffect(() => {
		if (!isLoaded) return
		updatePalette({ mode, colorScheme, darkTheme, lightTheme })
	}, [colorScheme, mode, isLoaded])

	useEffect(() => {
		setColorPalette(palette)
	}, [palette])

	return (
		<ThemeProvider theme={context}>
			<GlobalStyles />
			<Paddings />
			<Typography />
			{children}
		</ThemeProvider>
	)
}

export function useExtendedTheme(selector) {
	return useThemeStore(selector, shallow)
}

export default memo(Theme)
